"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import Link from "next/link";
import RiyalIcon from "@/assets/icons/RiyalIcon";
import defaultImage from "@/assets/icons/NewHomePage/default-event.png";
import tick from "@/assets/icons/NewHomePage/tick.svg";
import Cookies from "js-cookie";
import { useEventCardPrice } from "@/hooks/useEventCardPrice";

export default function NewProductCard({
  data,
  isSeller,
}: {
  data?: any;
  isSeller: boolean;
}) {
  const t = useTranslations();
  const normalizeCurrency = (value?: string) => {
    const normalized = (value || "").trim().toUpperCase();
    return ["SAR", "USD", "EUR"].includes(normalized) ? normalized : null;
  };
  const currency =
    normalizeCurrency(Cookies.get("currency")) ||
    normalizeCurrency(data?.currency) ||
    "SAR";

  const [imgSrc, setImgSrc] = useState(data?.image || defaultImage);
  const { displayPrice, cardRef } = useEventCardPrice(data, { isSeller });

  useEffect(() => {
    setImgSrc(data?.image || defaultImage);
  }, [data?.image]);

  const dateFromBackend: string | undefined = data?.date;

  const monthsMap: Record<string, number> = {
    يناير: 0,
    فبراير: 1,
    مارس: 2,
    أبريل: 3,
    مايو: 4,
    يونيو: 5,
    يوليو: 6,
    أغسطس: 7,
    سبتمبر: 8,
    أكتوبر: 9,
    نوفمبر: 10,
    ديسمبر: 11,
  };

  let dayName = "";

  if (dateFromBackend) {
    const [dayStr, monthName] = dateFromBackend.split(" ");
    const day = Number(dayStr);
    const year = new Date().getFullYear();

    const monthIndex = monthsMap[monthName];

    if (!isNaN(day) && monthIndex !== undefined) {
      const date = new Date(year, monthIndex, day);

      dayName = date.toLocaleDateString("ar-EG", {
        weekday: "long",
      });
    }
  }

  return (
    <Link
      ref={cardRef}
      href={
        isSeller
          ? `/seller/add-ticket/${data?.id}`
          : `/tickets/${data?.slug}/select-ticket`
      }
      className="flex flex-col justify-center items-start gap-4 w-full group"
    >
      <div className="relative rounded-lg overflow-hidden w-full">
        <Image
          src={imgSrc}
          alt="Product Image"
          width={300}
          height={200}
          className="w-full lg:min-h-[232px] min-h-[300px] max-h-[300px] lg:max-h-[232px] h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={() => setImgSrc(defaultImage)}
        />
        {data?.label && (
          <p
            style={{
              boxShadow: "0px 0px 4px 0px rgba(213, 175, 57, 1)",
            }}
            className="absolute top-3 right-3 bg-white shadow-[] font-medium border-0 sm:border sm:border-white/20 !text-black text-[14px] leading-[22px] px-[16px] py-[8px] rounded-[6px] shadow-sm"
          >
            {data?.label}
          </p>
        )}
      </div>

      <div className="flex flex-col items-start gap-[3px] ">
        <h3 className="text-[24px] font-extrabold   text-[#D1D3D4] whitespace-pre-wrap">
          {data?.title}
        </h3>

        <p
          className="text-[#D1D3D4] font-bold text-[24px] flex flex-row-reverse items-center gap-[5px]"
          dir="ltr"
        >
          {displayPrice}
          {currency === "SAR" && (
            <RiyalIcon
              width={15}
              height={16}
              className="inline-block align-middle"
            />
          )}
          {currency === "USD" && <span>$</span>}
          {currency === "EUR" && <span>€</span>}
        </p>

        {/* اليوم + التاريخ */}
        <p className="text-[#D1D3D4] text-[19px] font-medium ">
          {dayName && `${dayName} , `} {data?.date}
        </p>
        {data?.other_label && (
          <p className="text-[18px] font-medium !text-[#D4AF37] flex justify-center items-center gap-1">
            <Image
              src={tick}
              width={15}
              height={15}
              alt="tick"
              className="inline-block  "
            />
            {data?.other_label}
          </p>
        )}
      </div>
    </Link>
  );
}
